import {
  type EvidenceUploadMode,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceBulkDeleteCreate,
  useApiQualityExternalUploadV1EvidenceBulkDeleteCreate,
  useApiQualitySessionsV2TradeCompanySessionEvidenceBulkDeleteCreate,
} from 'api';
import { logger } from 'monitor';
import { useCallback, useMemo } from 'react';

type BulkDeletePayload = {
  data: {
    evidence_ids: number[];
  };
};

type UseBulkDeleteEvidenceArgs = {
  mode?: EvidenceUploadMode;
  onError?: (error: unknown) => void;
  onSuccess?: (evidenceIds: number[]) => void;
  parentId?: string | number;
};

export const useBulkDeleteEvidence = ({ mode, onError, onSuccess, parentId }: UseBulkDeleteEvidenceArgs) => {
  const { mutateAsync: deleteExternalLoadEvidence, isPending: isExternalLoadDeletePending } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceBulkDeleteCreate();
  const { mutateAsync: deleteExternalEvidence, isPending: isExternalEvidenceDeletePending } =
    useApiQualityExternalUploadV1EvidenceBulkDeleteCreate();
  const { mutateAsync: deleteTradeCompanySessionEvidence, isPending: isTradeCompanySessionDeletePending } =
    useApiQualitySessionsV2TradeCompanySessionEvidenceBulkDeleteCreate();

  const isPending = useMemo(() => {
    switch (mode) {
      case 'trade-company-session':
        return isTradeCompanySessionDeletePending;
      case 'external-evidence':
        return isExternalEvidenceDeletePending;
      case 'external-load':
        return isExternalLoadDeletePending;
      default:
        return false;
    }
  }, [isExternalEvidenceDeletePending, isExternalLoadDeletePending, isTradeCompanySessionDeletePending, mode]);

  const deleteEvidenceAsync = useCallback(
    async (evidenceIds: number[]): Promise<boolean> => {
      if (!mode || parentId === undefined || parentId === null) {
        logger.warn('Bulk delete requires a mode and parentId.', { evidenceIds, mode, parentId });
        return false;
      }

      try {
        switch (mode) {
          case 'trade-company-session':
            await deleteTradeCompanySessionEvidence({
              data: { evidence_ids: evidenceIds },
              tradeCompanySessionId: Number(parentId),
            });
            break;
          case 'external-evidence':
            await deleteExternalEvidence({
              data: { evidence_ids: evidenceIds },
              uuid: String(parentId),
            });
            break;
          case 'external-load':
            await deleteExternalLoadEvidence({
              data: { evidence_ids: evidenceIds },
              externalLoadId: Number(parentId),
            });
            break;
          default:
            logger.warn('Unsupported bulk delete mode.', { evidenceIds, mode, parentId });
            return false;
        }

        onSuccess?.(evidenceIds);
        return true;
      } catch (error) {
        onError?.(error);
        return false;
      }
    },
    [
      deleteExternalEvidence,
      deleteExternalLoadEvidence,
      deleteTradeCompanySessionEvidence,
      mode,
      onError,
      onSuccess,
      parentId,
    ]
  );

  const deleteEvidence = useCallback(
    ({ data }: BulkDeletePayload) => {
      void deleteEvidenceAsync(data.evidence_ids);
    },
    [deleteEvidenceAsync]
  );

  return { deleteEvidence, deleteEvidenceAsync, isPending };
};
